import { useEffect, useState } from "react";
import { Button } from "@mui/material";

import "./style.scss";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";

const formatRemaining = (expiresAt: Date, now: number): string => {
    const ms = expiresAt.getTime() - now;
    if (ms <= 0) return "expired";
    const minutes = Math.ceil(ms / 60000);
    if (minutes >= 60) {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return m ? `${h} h ${m} min left` : `${h} h left`;
    }
    return `${minutes} min left`;
};

/**
 * Persistent banner while a super-admin support session is active
 * (tenant_authentication_plan.md §2.5). Rendered above every route so the
 * person in the tenant is never unsure whose session they are in. Counts
 * down to the server-enforced expiry; when it passes, the API starts
 * refusing the token and the normal 401 handling signs the session out.
 */
export const SupportSessionBanner = () => {
    const { supportSession, logout } = FirebaseAuthContainer.useContainer();
    const [now, setNow] = useState<number>(Date.now());

    useEffect(() => {
        if (!supportSession) return;
        const timer = window.setInterval(() => setNow(Date.now()), 30000);
        return () => window.clearInterval(timer);
    }, [supportSession]);

    if (!supportSession) return null;

    const tenant = window.location.hostname.split(".")[0];

    return (
        <div className="support-session-banner" role="status" aria-live="polite">
            <span className="support-session-banner__text">
                <strong>Support session</strong>
                {tenant ? ` — ${tenant}` : ""} · acting as {supportSession.actor} ·{" "}
                {formatRemaining(supportSession.expiresAt, now)}
            </span>
            <Button
                className="support-session-banner__end"
                variant="text"
                size="small"
                onClick={() => {
                    logout();
                    window.close();
                }}
            >
                End session
            </Button>
        </div>
    );
};
